import { SanityClient } from '@sanity/client'

import {
  BootcampBossData,
  BootcampLevelArg,
  CategoriesData,
  SubjectDefaultName,
  VccData,
} from '~/bootcamp/types/bootcamp.types'
import { BootcampChestsData, bootcampsListGroq, SubjectsListGroq } from '~/types/bootcamp'
import { BadgeImage } from '~/shared/types/bootcampBadge.types'
import {
  bootcampBossGroq,
  gemsOfModuleGroq,
  overviewGroq,
  categoriesDataGroq,
  subjectNameByKeyAndCategoryGroq,
  subjectListWithCourseListGroq,
  overallGroq,
  firstSubjectKeyGroq,
  getThumbnailGroq,
} from '~/services/bootcampQuries.service'

import { bootcampCategoryLanguageKeyMap } from '~/shared/constants/bootcampCategoryKeyMap.const'

import { BossBattleEnemy } from '~/bootcamp/components/Quiz/QuizProps.models'

import sanityClient from './sanity-client'

class BootcampSanity {
  private client: SanityClient

  public constructor() {
    this.client = sanityClient
  }

  /**
   * This function take lang as input and return all Subject list with all courses as array
   * @param param0 // Lang version => "en" or "no"
   * @returns {Object} listSubjectsWithCourses Bootcamp Subjects List Data with English Subjects Data as a reference.
   * @returns {Array} listBootcampReturnObj.subjects Bootcamp Subjects Data of the provided locale
   * @returns {Array} listBootcampReturnObj.reference English Bootcamp Subjects Data
   */
  public listSubjectsWithCourses({
    language,
    gymCategory,
  }: {
    language: string
    gymCategory: string
  }): Promise<SubjectsListGroq> {
    return this.client.fetch(subjectListWithCourseListGroq, {
      language,
      gymCategory,
    })
  }
  public firstSubjectKey({
    language,
    gymCategory,
  }: {
    language: string
    gymCategory: string
  }): Promise<{ subjectKey: string }> {
    return this.client.fetch(firstSubjectKeyGroq, {
      language,
      gymCategory,
    })
  }

  /**
   * This function is to get bootcamp overview with module Design data.
   * @param param0 //URL key & language
   * @returns Bootcamp Overview Data
   */
  public bootcampsList({ key, language }: { key: string; language: string }): Promise<bootcampsListGroq | null> {
    return this.client.fetch(overviewGroq, {
      key,
      language,
    })
  }

  public getVccThumbnail({ key, module, language, level }: BootcampLevelArg): Promise<VccData> {
    return this.client.fetch(getThumbnailGroq, {
      key,
      module,
      language,
      level,
    })
  }

  public getVccSanityData({ key, module, language, level }: BootcampLevelArg): Promise<VccData> {
    return this.client.fetch(overallGroq, {
      key,
      module,
      language,
      level,
    })
  }

  public getBootcampGemData({
    key,
    module,
    language,
  }: {
    key: string
    module: string
    language: string
  }): Promise<BootcampChestsData[] | null> {
    return this.client.fetch(gemsOfModuleGroq, {
      key,
      module,
      language,
    })
  }

  /**
   * TO get data for Bootcamp Boss of Bootcamp Quiz
   * @param param0 Object
   * @returns Return array of level ->activity->(DifficultyLevel & ActivityName)
   */
  public getBootcampBossData({ key, language, module }: { key: string; language: string; module: string }): Promise<{
    levels: BootcampBossData[] | null
    taskId: string
    badgeImage: BadgeImage
    bossBattleEnemy: BossBattleEnemy
  }> {
    return this.client.fetch(bootcampBossGroq, {
      key,
      language,
      module,
    })
  }

  /**
   * Get number of gyms/subjects are in a Bootcamp Category
   *
   * @param language Locale key
   * @returns {Object} Object where keys are category name and value are numbers of gyms available on that category
   */

  public getCategoriesData({ language }: { language: string }): Promise<CategoriesData | null> {
    return this.client.fetch(categoriesDataGroq, { language })
  }

  /**
   * Get All bootcamp key and name
   * @param language
   * @param gymCategory
   * @returns
   */
  public async getSubjectAndCategoryName({
    language,
    bootcampCategory,
    key,
  }: {
    language: string
    bootcampCategory: string
    key: string
  }): Promise<SubjectDefaultName> {
    const data = await this.client.fetch(subjectNameByKeyAndCategoryGroq, {
      language,
      bootcampCategory,
      key,
    })
    data['categoryLanguageKey'] = bootcampCategoryLanguageKeyMap[bootcampCategory]
    return data
  }
}

export const bootcampSanity = new BootcampSanity()
